import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Trans } from 'react-i18next'

import { Details, Search, TableHeader } from '@/components'
import { invoicesPath, makeSearchPath, sort } from '@/utils'
import { InvoiceNode } from '@/apollo/graphql'
import { OrderType } from '@/types'
import { useApp } from '@/hooks'
import { t } from '@/i18n'

import InvoiceInfo from './components/invoiceInfo'
import InvoiceRow from './components/InvoiceRow'
import { useSelect } from './useSelect'
import { useData } from './useData'

const columns = [
  {
    key: 'invoice_number',
    sortKey: 'id',
  },
  {
    key: 'deal_id',
    sortKey: 'dealIds',
  },
  // {
  //   key: 'clients_rate',
  // },
]

const Invoices: React.FC = () => {
  const { id, customerId } = useParams()
  const { currency } = useApp()
  const navigate = useNavigate()
  const { data: selectedData } = useData({ id })
  const _customerId = useMemo(
    () => (customerId === 'customer' ? undefined : Number(customerId)),
    [customerId],
  )
  const [sortKey, setKey] = useState<string>()
  const [orderBy, setOrderBy] = useState<OrderType>()
  const [selectedInvoices, setInvoices] = useState<Set<InvoiceNode>>(
    new Set([]),
  )
  const { onSearch, data, pageInfo, onFetchMore, loading } =
    useSelect(_customerId)

  const HeaderComponent = useMemo(
    () => <Search onSearch={onSearch} />,
    [onSearch],
  )

  const onClose = useCallback(() => {
    navigate(makeSearchPath(invoicesPath, { customerId: _customerId }))
  }, [navigate, _customerId])

  const sortingBy = useCallback((key?: string, by?: OrderType) => {
    setKey(key)
    setOrderBy(by)
  }, [])

  const InvoicesList = useMemo(() => {
    return (
      <Details
        showNoData
        Row={props => (
          <InvoiceRow
            {...props}
            onSelect={node =>
              setInvoices(prev => {
                if (prev.has(node)) {
                  prev.delete(node)
                  return new Set([...prev])
                }
                return new Set([...prev, node])
              })
            }
          />
        )}
        Wrapper="table"
        RowParent="tbody"
        TitleWrapper="h3"
        loading={loading}
        title={t('invoices')}
        onFetchMore={onFetchMore}
        listWrapperClassName="list-items"
        wrapperClassName="info-box w-100"
        HeaderComponent={HeaderComponent}
        dataBoxWrapperClassName="info-table"
        fetchMoreStatus={pageInfo.fetchMoreStatus}
        data={data.sort((a, b) => sort(a, b, sortKey, orderBy))}
        Header={() => (
          <TableHeader
            columns={columns}
            onSort={sortingBy}
            orderedBy={orderBy}
          />
        )}
      />
    )
  }, [
    HeaderComponent,
    data,
    loading,
    onFetchMore,
    orderBy,
    pageInfo.fetchMoreStatus,
    sortKey,
    sortingBy,
  ])

  return (
    <div className="row">
      <div className={`col w-${id ? '45' : '100'}`}>{InvoicesList}</div>
      <div className="col w-55">
        <InvoiceInfo node={id ? selectedData : undefined} onClose={onClose} />
      </div>

      {!!selectedInvoices.size && (
        <div className="col w-100">
          <div className="invoice-info">
            <p>
              {/* @ts-ignore */}
              <Trans t={t} i18nKey="selected_invoices">
                {/* @ts-ignore */}
                <b>{{ size: selectedInvoices.size }}</b>
                <span>
                  {{
                    // @ts-ignore
                    currency,
                    total: [...selectedInvoices].reduce(
                      (prev, current) => prev + current.totalPrice,
                      0,
                    ),
                  }}
                </span>
              </Trans>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default Invoices
